import type { ISmartySuggestion } from '~/types/smarty/models'

//
// A small fixed set of US addresses for offline development.
//
// Deliberately includes one multi-unit building (`entries > 1`) plus the unit
// list it expands into, so the secondary-expansion path can be exercised
// without a Smarty key or a network.
//

function suggestion(
  overrides: Partial<ISmartySuggestion> & { street_line: string }
): ISmartySuggestion {
  return {
    secondary: '',
    city: 'Denver',
    state: 'CO',
    zipcode: '80202',
    entries: 1,
    ...overrides,
  }
}

const SUGGESTIONS: ISmartySuggestion[] = [
  suggestion({
    street_line: '1600 Pennsylvania Ave NW',
    city: 'Washington',
    state: 'DC',
    zipcode: '20500',
  }),
  suggestion({
    street_line: '1600 Amphitheatre Pkwy',
    city: 'Mountain View',
    state: 'CA',
    zipcode: '94043',
  }),
  // The multi-unit case.
  suggestion({
    street_line: '1700 Broadway',
    entries: 4,
  }),
  suggestion({
    street_line: '410 17th St',
    zipcode: '80202',
  }),
  suggestion({
    street_line: '1201 16th St Mall',
    zipcode: '80202',
  }),
  suggestion({
    street_line: '350 Fifth Ave',
    city: 'New York',
    state: 'NY',
    zipcode: '10118',
  }),
]

// The units 1700 Broadway expands into when picked.
const UNITS_BY_STREET_LINE: Record<string, ISmartySuggestion[]> = {
  '1700 Broadway': ['Ste 100', 'Ste 1200', 'Ste 1900', 'Ste 2000'].map((s) =>
    suggestion({ street_line: '1700 Broadway', secondary: s, entries: 1 })
  ),
}

export function getSmartySuggestions(search: string): ISmartySuggestion[] {
  const needle = (search || '').trim().toLowerCase()
  if (!needle) {
    return []
  }
  return SUGGESTIONS.filter((s) =>
    `${s.street_line} ${s.city} ${s.state} ${s.zipcode}`
      .toLowerCase()
      .includes(needle)
  )
}

// `selected` arrives in Smarty's documented format:
//   "<street_line> <secondary> (<entries>) <city> <state> <zipcode>"
// Matching on a street-line prefix is enough for a mock.
export function getSmartyUnitsForSelected(
  selected: string
): ISmartySuggestion[] {
  const match = Object.keys(UNITS_BY_STREET_LINE).find((streetLine) =>
    selected.startsWith(streetLine)
  )
  return (match ? UNITS_BY_STREET_LINE[match] : []) ?? []
}

//
// US Street (verification) mock — services/smarty/usStreet.ts.
//
// Built from the SAME fixed address set above, so an address picked from the
// mock autocomplete then verifies cleanly, exactly as it does live.
//
// The fixtures are chosen to reach every branch of buildVerificationResult
// offline: a clean match, a spelling correction, a building that needs a unit,
// and a unit that does not exist. Without this handler mock-mode blur would
// reach the real, paid API — the worker runs `onUnhandledRequest: 'bypass'`.
//

function componentsFromStreetLine(streetLine: string) {
  // A mock's convenience, not a real parser: first token is the number and the
  // last is the suffix. What matters is that streetLineFromComponents()
  // reassembles exactly this line, so the round-trip is faithful.
  const parts = streetLine.split(' ')
  return {
    primary_number: parts[0] ?? '',
    street_name: parts.slice(1, -1).join(' '),
    street_suffix: parts.length > 1 ? parts[parts.length - 1] ?? '' : '',
  }
}

const normalizeStreet = (v: string) =>
  (v || '').toUpperCase().replace(/[.,#]/g, '').replace(/\s+/g, ' ').trim()

/** Loose match: house number plus the first word of the street name. */
function findFixtureForStreet(street: string): ISmartySuggestion | undefined {
  const typed = normalizeStreet(street).split(' ')
  if (typed.length < 2) {
    return undefined
  }
  const [number, firstWord] = typed
  return SUGGESTIONS.find((s) => {
    const canonical = normalizeStreet(s.street_line).split(' ')
    return canonical[0] === number && canonical[1] === firstWord
  })
}

export function getSmartyStreetCandidates({
  street,
  secondary,
}: {
  street: string
  secondary?: string
}): object[] {
  const fixture = findFixtureForStreet(street)
  if (!fixture) {
    return [] // Smarty's documented "no match" answer.
  }

  const knownUnits = (UNITS_BY_STREET_LINE[fixture.street_line] ?? []).map(
    (u) => normalizeStreet(u.secondary)
  )
  const typedUnit = normalizeStreet(secondary || '')
  const isMultiUnit = Number(fixture.entries) > 1

  // D = the building requires a unit and none was given.
  // S = a unit was given, but this building has no such unit.
  let dpvMatchCode = 'Y'
  if (isMultiUnit && !typedUnit) {
    dpvMatchCode = 'D'
  } else if (
    typedUnit &&
    knownUnits.length &&
    !knownUnits.includes(typedUnit)
  ) {
    dpvMatchCode = 'S'
  }

  const matchedUnit = (UNITS_BY_STREET_LINE[fixture.street_line] ?? []).find(
    (u) => normalizeStreet(u.secondary) === typedUnit
  )
  const unit = matchedUnit?.secondary ?? ''
  const [designator, ...numberParts] = unit ? unit.split(' ') : []

  return [
    {
      delivery_line_1: [fixture.street_line, unit].filter(Boolean).join(' '),
      last_line: `${fixture.city} ${fixture.state} ${fixture.zipcode}-6789`,
      components: {
        ...componentsFromStreetLine(fixture.street_line),
        ...(unit && {
          secondary_designator: designator,
          secondary_number: numberParts.join(' '),
        }),
        city_name: fixture.city,
        state_abbreviation: fixture.state,
        zipcode: fixture.zipcode,
        // A fixed +4 so the ZIP+4 rule in buildVerificationResult is exercised
        // offline: this must NOT on its own raise a "did you mean".
        plus4_code: '6789',
      },
      analysis: { dpv_match_code: dpvMatchCode },
    },
  ]
}
